import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import '../App.css';

// instantiate services and wire up dependencies
import AuthSource from 'core/services/authSource';
import DataSource from 'core/services/dataSource';
import HTTPService from 'core/services/http';
import createStore from 'core/store';
import { Suspense, lazy, useState } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { LinearProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
// zippedi tools js imports
import { ZtoolSnackAlert as SnackAlert } from '@zippeditoolsjs/blocks';
import { StoreWalk, StoreWalkSource } from '@zippeditoolsjs/virtual-store-walk';
import { ReduxLoginWeb as PrivateRoute, darkTheme, lightTheme } from '@zippeditoolsjs/web';

import ClientSuppliers from './components/userManagement/suppliers/ClientSuppliers';
// Contexts
import { DigitalShelfProvider } from './contexts/DigitalShelf';
import Dashboards from './redux/containers/features/dashboards/Dashboards';
import DigitalShelf from './redux/containers/features/digitalShelf/DigitalShelf';
import DigitalStore from './redux/containers/features/digitalStore/DigitalStore';
import LastProductState from './redux/containers/features/lastProductState/LastProductState';
import LastProductStateLegacy from './redux/containers/features/lastProductState/LastProductStateLegacy';
import Notifications from './redux/containers/features/notifications/Notifications';
import TrackingList from './redux/containers/features/trackingList/TrackingList';
import Home from './redux/containers/home/Home';
import Link from './redux/containers/link/Link';
import Layout from './redux/containers/navbar/Layout';
import Search from './redux/containers/search/Search';
import Translations from './redux/containers/translations/Translations';
import Bruno from './redux/containers/userManagement/bruno/Bruno';
import Contacts from './redux/containers/userManagement/contacts/Contacts';
import Users from './redux/containers/userManagement/users/Users';
import { addMissingStyles } from './stylesSheets';

const ApiDocsContainer = lazy(() => import('./redux/containers/documentation/ApiDocsContainer'));

const { REACT_APP_API_BASE_URL, REACT_APP_AUTH_BASE_URL, REACT_APP_BASE_URL, REACT_APP_AUTH_BACKEND_URL, REACT_APP_VIRTUAL_STORE_WALK_URL } =
  process.env;

const http = new HTTPService();

const dataSource = new DataSource({
  baseUrl: REACT_APP_API_BASE_URL,
  http,
});

const authSource = new AuthSource({
  baseUrl: REACT_APP_AUTH_BACKEND_URL,
  http,
});

const storeWalkSource = new StoreWalkSource({
  baseUrl: REACT_APP_VIRTUAL_STORE_WALK_URL,
  http,
})


const store = createStore({
  authSource,
  dataSource,
  storeWalkSource,
});

const App = () => {
  const [theme, setTheme] = useState('light');
  const isDarkTheme = theme === 'dark';
  const toggleTheme = () => setTheme(isDarkTheme ? 'light' : 'dark');

  const { i18n } = useTranslation();
  const toogleTranslation = lang => i18n.changeLanguage(lang);

  return (
    <Provider store={store}>
      <ThemeProvider theme={isDarkTheme ? addMissingStyles(darkTheme) : addMissingStyles(lightTheme)}>
        <SnackAlert />
        <PrivateRoute
          appUrlLogin={REACT_APP_AUTH_BASE_URL}
          tenattype={'ext'}
          appBaseUrl={REACT_APP_BASE_URL}>
          <Router>
            <Layout
              isDarkTheme={isDarkTheme}
              toggleTheme={toggleTheme}
              toogleTranslation={toogleTranslation}>
              <Routes>
                {/* Main Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/cpg/home" element={<Home />} />
                <Route path="/cpg/walls" element={
                  <DigitalShelfProvider>
                    <DigitalShelf />
                  </DigitalShelfProvider>
                } />
                <Route path="/cpg/trackingList" element={<TrackingList />} />
                <Route path="/cpg/digitalStore" element={<DigitalStore />} />
                <Route path="/cpg/new/lastProductState" element={<LastProductState />} />
                <Route path="/cpg/lastProductState" element={<LastProductStateLegacy />} />
                <Route path="/cpg/dashboards" element={<Dashboards />} />
                <Route path="/cpg/demo/store_walk" element={<StoreWalk />}></Route>
                <Route
                  exact
                  path="/cpg/docs"
                  element={
                    <Suspense fallback={<LinearProgress color="secondary" sx={{ borderRadius: '10rem', mt: 4 }} />}>
                      <ApiDocsContainer />
                    </Suspense>
                  }
                />

                {/* Assignment Routes */}
                <Route path="/cpg/admin/trackingList" element={<TrackingList />} />

                {/* User Accounts Routes */}
                <Route path="/cpg/admin/users" element={<Users />} />
                <Route path="/cpg/admin/clients" element={<ClientSuppliers />} />
                <Route path="/cpg/admin/bruno" element={<Bruno />} />
                <Route path="/cpg/admin/contacts" element={<Contacts />} />

                {/* Settings */}
                <Route path="/cpg/admin/translations" element={<Translations />} />

                {/* Notifications */}
                <Route path="/cpg/admin/notifications" element={<Notifications />} />

                {/* Extras */}
                <Route path="/search" element={<Search />} />
                <Route path="/share" element={<Link />} />
              </Routes>
            </Layout>
          </Router>
        </PrivateRoute>
      </ThemeProvider>
    </Provider>
  );
};

export default withTranslation()(App);
